/* Vital Affiliate Wizard v2 — Alpine controller */

(function() {
  'use strict';

  /* Visitor-facing words come from AFFREG_CONFIG.strings (sections/affiliate-registration.liquid, the store's language); [detail]/[name] are placeholders. The English here is the fallback. */
  function S(key, fallback, vars) {
    const c = window.AFFREG_CONFIG || {};
    let s = (c.strings && c.strings[key]) || fallback;
    Object.keys(vars || {}).forEach(function (k) { s = s.replace('[' + k + ']', function () { return vars[k]; }); });
    return s;
  }

  const SS_KEY_STEP    = 'vital_wizard_step';
  const SS_KEY_SPONSOR = 'vital_wizard_sponsor';
  const SS_KEY_COUNTRY = 'vital_wizard_country';
  const SS_KEY_PACK    = 'vital_wizard_pack_chosen';
  const SS_KEY_RETURN  = 'vital_wizard_return';

  function readJSON(key) {
    try { const v = sessionStorage.getItem(key); return v ? JSON.parse(v) : null; }
    catch (e) { return null; }
  }
  function writeJSON(key, val) {
    try { sessionStorage.setItem(key, JSON.stringify(val)); } catch (e) {}
  }
  function readStr(key) {
    try { return sessionStorage.getItem(key) || ''; } catch (e) { return ''; }
  }
  function writeStr(key, val) {
    try { sessionStorage.setItem(key, val); } catch (e) {}
  }
  function readCookie(name) {
    const m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return m ? decodeURIComponent(m.pop()) : '';
  }
  function setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + (days || 30) * 24 * 60 * 60 * 1000);
    document.cookie = name + '=' + encodeURIComponent(value || '') + ';expires=' + d.toUTCString() + ';path=/';
  }
  function urlParam(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart fetch failed');
    return res.json();
  }
  async function updateCartAttributes(attrs) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: attrs })
    });
    if (!res.ok) throw new Error('cart update failed');
    return res.json();
  }
  async function addToCart(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    });
    if (!res.ok) {
      let msg = 'cart add failed';
      try { const err = await res.json(); msg = err.description || err.message || msg; } catch (e) {}
      throw new Error(msg);
    }
    const body = await res.json();
    // Theme's validateCartAdd interceptor returns 200 OK with { _blocked: true } when
    // a swap/conflict modal was declined or a duplicate was detected. Without this check,
    // the wizard would advance with stale state and the user's click silently no-ops.
    if (body && body._blocked === true) {
      const err = new Error(S('blocked', 'Cart add was blocked. Please review your cart or pick a different option.'));
      err.blocked = true;
      throw err;
    }
    return body;
  }
  async function changeCart(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: line, quantity: quantity })
    });
    if (!res.ok) throw new Error('cart change failed');
    return res.json();
  }

  function packPresent(cart) {
    if (!cart || !cart.items) return false;
    for (let item of cart.items) {
      const props = item.properties || {};
      if (props._bundle_pack || props._bbg_builder === '1' || props._bbg_builder === 1) return true;
    }
    return false;
  }
  function prebuiltPackInCart(cart) {
    if (!cart || !cart.items) return false;
    for (let item of cart.items) {
      if (item.properties && item.properties._bundle_pack) return true;
    }
    return false;
  }
  function customPackInCart(cart) {
    if (!cart || !cart.items) return false;
    for (let item of cart.items) {
      const v = item.properties && item.properties._bbg_builder;
      if (v === '1' || v === 1) return true;
    }
    return false;
  }
  function wizardAddonsInCart(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.filter(i => i.properties && i.properties._wizard_addon === '1');
  }

  window.affiliateWizard = function(config) {
    return {
      // ---- state ----
      config: config || {},
      currentStep: 1,            // Default — overridden sync in init() to prevent Step-1 flash
      sponsor: null,            // { refID, slug, fullName }
      sponsorQuery: '',
      sponsorManuallyEntered: false,
      sponsorError: '',
      sponsorLoading: false,
      sponsorMode: 'confirm',   // 'confirm' | 'search'
      country: null,            // ISO code
      countrySelection: '',     // Step 1 dropdown binding (before Continue is clicked)
      cart: { items: [], item_count: 0 },
      packChosen: null,         // 'prebuilt' | 'custom' | null
      banner: '',
      isMutating: false,        // double-click guard
      loadingMessage: '',       // when non-empty, shows the full-viewport loading overlay

      // ---- lifecycle ----
      async init() {
        console.log('[Affiliate Wizard] init', this.config);

        // 0. Wire up the pack-details modal with vanilla JS (Alpine teleport/body-move
        //    breaks bindings; vanilla is simpler and bulletproof).
        const self = this;
        const modal = document.getElementById('affreg-pack-modal');
        if (modal) {
          if (modal.parentElement !== document.body) document.body.appendChild(modal);
          modal.querySelector('[data-affreg-modal-close]').addEventListener('click', () => self._hideModal());
          modal.addEventListener('click', (e) => { if (e.target === modal) self._hideModal(); });
          modal.querySelector('[data-affreg-modal-choose]').addEventListener('click', () => self.chooseFromModal());
          document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && modal.style.display !== 'none') self._hideModal(); });
          // Force wheel to scroll the inner modal (theme's global scroll handlers don't reach this)
          modal.addEventListener('wheel', (e) => {
            const inner = modal.querySelector('.affreg-modal');
            if (!inner) return;
            inner.scrollTop += e.deltaY;
            e.preventDefault();
            e.stopPropagation();
          }, { passive: false });
        }
        // Move scanner-offer modal to body too so it can overlay everything correctly.
        const sModal = document.getElementById('affreg-scanner-modal');
        if (sModal && sModal.parentElement !== document.body) document.body.appendChild(sModal);

        // 1. URL params
        const urlRefID  = urlParam('refID');
        const urlSlug   = urlParam('slug');
        const urlSide   = urlParam('side');
        const urlStep   = parseInt(urlParam('step'), 10);

        // Synchronously set a first-guess step BEFORE the async cart fetch, to prevent
        // a brief Step-1 flash for returning users / URL-driven navigation.
        // The full prereq check below may refine this after cart resolves.
        if (!Number.isNaN(urlStep) && urlStep >= 1 && urlStep <= 5) {
          this.currentStep = urlStep;
        } else {
          const savedStepGuess = parseInt(readStr(SS_KEY_STEP), 10);
          if (!Number.isNaN(savedStepGuess) && savedStepGuess >= 1 && savedStepGuess <= 5) {
            this.currentStep = savedStepGuess;
          }
        }
        // Market selector modal sets BOTH `selectedCountry` and `country` on redirect.
        // Prefer `selectedCountry` (set by modal explicitly); fall back to `country` (Shopify localization).
        const urlCountry = urlParam('selectedCountry') || urlParam('country');
        const cameFromMarketSelector = urlParam('marketSelected') === 'true';

        if (urlSide && (urlSide.toUpperCase() === 'L' || urlSide.toUpperCase() === 'R')) {
          setCookie('side', urlSide.toUpperCase(), 30);
        }

        // 2. Cookies
        const cookRefID = readCookie('refID');
        const cookSlug = readCookie('slug');
        const cookReferredBy = readCookie('referredBy');

        // 3. SessionStorage
        const savedSponsor = readJSON(SS_KEY_SPONSOR);
        const savedCountry = readStr(SS_KEY_COUNTRY);
        const savedStep = parseInt(readStr(SS_KEY_STEP), 10);
        const savedPack = readStr(SS_KEY_PACK);

        // 4. Resolve sponsor
        if (urlRefID && urlSlug) {
          this.sponsor = { refID: urlRefID, slug: urlSlug, fullName: cookReferredBy || urlSlug };
          setCookie('refID', urlRefID, 30);
          setCookie('slug', urlSlug, 30);
          if (cookReferredBy) setCookie('referredBy', cookReferredBy, 30);
        } else if (urlRefID) {
          // Incoming referral redirects (my.vitalhealthglobal.com/USERNAME → ?refID=USERID)
          // arrive with the numeric User ID only. Look up slug + fullName from the endpoint.
          this._lookupSponsor(urlRefID).then(s => {
            if (s) {
              this.sponsor = s;
              setCookie('refID', s.refID, 30);
              setCookie('slug', s.slug, 30);
              setCookie('referredBy', s.fullName, 30);
              writeJSON(SS_KEY_SPONSOR, s);
            }
          });
        } else if (savedSponsor) {
          this.sponsor = savedSponsor;
        } else if (cookRefID && cookSlug) {
          this.sponsor = { refID: cookRefID, slug: cookSlug, fullName: cookReferredBy || cookSlug };
        }

        // 5. Resolve country
        if (urlCountry) {
          this.country = urlCountry.toUpperCase();
          writeStr(SS_KEY_COUNTRY, this.country);
          // If user just returned from the market selector, write lang to cart attrs once.
          // The /cart/update.js call is idempotent so harmless if it re-fires.
          if (cameFromMarketSelector && this.config.lang) {
            this.writeCountryAttrs(this.config.lang).catch(err => {
              console.warn('[Affiliate Wizard] lang attr write failed', err);
            });
          }
        } else if (savedCountry) {
          this.country = savedCountry;
        }

        // 6. Restore pack hint
        this.packChosen = savedPack || null;

        // 7. Fetch cart (authoritative for pack state)
        try {
          this.cart = await fetchCart();
        } catch (e) {
          this.banner = S('sync_fail', 'Trouble syncing your cart. Refresh if this persists.');
          console.warn('[Affiliate Wizard] cart fetch failed', e);
        }

        // 7a. Backfill cart.attributes.country for returning users (sessionStorage/URL country
        //     resolved but cart attr missing) — Step 5 Liquid needs it for PV/GV market detection.
        if (this.country && this.cart && this.cart.attributes &&
            (this.cart.attributes.country || '').toUpperCase() !== this.country.toUpperCase()) {
          updateCartAttributes({ country: this.country })
            .then(() => this.refreshCart())
            .catch(err => console.warn('[Affiliate Wizard] country backfill failed', err));
        }

        // 8. Compute starting step
        const hasPack = packPresent(this.cart);
        const membershipPid = this.config.membershipProductId;
        const membershipVid = this.config.membershipVariantId;
        const hasMembershipInCart = this.cart && this.cart.items && this.cart.items.some(li =>
          (membershipPid && li.product_id === membershipPid) ||
          (membershipVid && li.variant_id === membershipVid)
        );
        // Membership-only mode = user chose "Enroll Now" or removed their pack but kept membership.
        // Treat as equivalent to "pack chosen" for step-gating so Step 4/5 stay accessible.
        const membershipOnlyMode = savedPack === 'membership-only' && hasMembershipInCart;
        const hasPackOrMembership = hasPack || membershipOnlyMode;
        let computed = 1;
        if (hasPackOrMembership) {
          computed = 4;
        } else if (this.sponsor && this.country) {
          computed = 3;
        } else if (this.country) {
          computed = 2;
        }

        // Honor URL ?step= only if it doesn't skip prerequisites.
        // Step 5 (summary) shares the same prereq as Step 4, so allow it forward.
        if (!Number.isNaN(urlStep) && urlStep >= 1 && urlStep <= 5) {
          if (urlStep <= computed) {
            computed = urlStep;
          } else if (urlStep === 5 && hasPackOrMembership) {
            computed = 5;
          } else {
            console.log('[Affiliate Wizard] ignoring ?step=' + urlStep + ', falling back to ' + computed);
          }
        } else if (savedStep === 5 && hasPackOrMembership) {
          // Step 5 (review) shares prereqs with Step 4 — allow promote on reload.
          computed = 5;
        }
        // Note: we intentionally DON'T promote savedStep > computed for steps 2-4.
        // If state has degraded (e.g. sessionStorage country missing on a fresh tab),
        // computed reflects the max legitimate step. Promoting past it lands the user
        // on a step whose prerequisites aren't met (e.g. Step 3 with no country → no
        // packs visible because packCardVisible() requires this.country).

        this.currentStep = computed;
        writeStr(SS_KEY_STEP, String(this.currentStep));
        console.log('[Affiliate Wizard] starting at step', this.currentStep);

        this._syncAddonsFromCart();
        this._initActiveCollection();
        this._animateHeroStatsIfStep5();
        this._celebrateStep5();
        this._weglotRescan();
      },

      // Nudge Weglot to discover/translate any wizard text that wasn't in the DOM
      // when its initial scan ran. Safe no-op if Weglot isn't loaded.
      _weglotRescan() {
        const tryScan = () => {
          const W = window.Weglot;
          if (!W) return;
          try {
            if (typeof W.search === 'function') W.search();
            else if (typeof W.translate === 'function' && typeof W.getCurrentLang === 'function') {
              W.translate({ words: [], languageTo: W.getCurrentLang() });
            }
          } catch (e) { /* swallow */ }
        };
        // Run now and again after Alpine settles + after any x-show transitions.
        tryScan();
        setTimeout(tryScan, 250);
        setTimeout(tryScan, 1000);
      },

      // Soft haptic on success — no-op on iOS Safari (no Vibration API) but works on Android.
      _pulse(ms = 35) {
        try { if (navigator.vibrate) navigator.vibrate(ms); } catch (e) {}
      },

      // +1 "fly to cart" animation: spawn a transient pill at the clicked button position,
      // animate it toward the top "Next: Review your cart" button. Cleans itself up.
      _flyToNext(sourceEl) {
        if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const target = document.querySelector('.affreg-next-top');
        if (!target || !sourceEl) return;
        const src = sourceEl.getBoundingClientRect();
        const dst = target.getBoundingClientRect();
        const fly = document.createElement('span');
        fly.className = 'affreg-fly-plus';
        fly.textContent = '+1';
        fly.setAttribute('aria-hidden', 'true');
        fly.style.left = (src.left + src.width / 2) + 'px';
        fly.style.top  = (src.top + src.height / 2) + 'px';
        document.body.appendChild(fly);
        // Force layout so the transition takes effect
        void fly.offsetWidth;
        const dx = (dst.left + dst.width / 2) - (src.left + src.width / 2);
        const dy = (dst.top  + dst.height / 2) - (src.top + src.height / 2);
        fly.style.transform = `translate(${dx}px, ${dy}px) scale(0.4)`;
        fly.style.opacity = '0';
        const cleanup = () => fly.remove();
        fly.addEventListener('transitionend', cleanup, { once: true });
        setTimeout(cleanup, 900); // safety net
      },

      // Confetti burst on Step 5 first-load (once per session, prefers-reduced-motion aware).
      _celebrateStep5() {
        if (this.currentStep !== 5) return;
        try { if (sessionStorage.getItem('vital_wizard_celebrated')) return; } catch (e) {}
        if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const canvas = document.getElementById('affreg-confetti');
        if (!canvas) return;
        try { sessionStorage.setItem('vital_wizard_celebrated', '1'); } catch (e) {}

        const ctx = canvas.getContext('2d');
        const host = canvas.parentElement;
        const sizeCanvas = () => {
          const r = host.getBoundingClientRect();
          canvas.width = r.width;
          canvas.height = Math.max(280, r.height);
        };
        sizeCanvas();

        const colors = ['#15803d', '#22c55e', '#84cc16', '#facc15', '#fef3c7', '#ffffff', '#bef264'];
        const N = 90;
        const particles = [];
        for (let i = 0; i < N; i++) {
          particles.push({
            x: canvas.width / 2 + (Math.random() - 0.5) * 160,
            y: 30 + Math.random() * 40,
            vx: (Math.random() - 0.5) * 9,
            vy: -Math.random() * 13 - 3,
            r: 4 + Math.random() * 5,
            color: colors[Math.floor(Math.random() * colors.length)],
            angle: Math.random() * Math.PI * 2,
            angleVel: (Math.random() - 0.5) * 0.35,
            shape: Math.random() < 0.5 ? 'rect' : 'circle',
          });
        }
        const start = performance.now();
        const duration = 2400;
        const gravity = 0.42;
        const drag = 0.991;
        const tick = (now) => {
          const t = (now - start) / duration;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          if (t >= 1) {
            canvas.style.display = 'none';
            return;
          }
          const opacity = Math.max(0, 1 - Math.pow(t, 1.5));
          for (const p of particles) {
            p.vy += gravity;
            p.vx *= drag;
            p.x += p.vx;
            p.y += p.vy;
            p.angle += p.angleVel;
            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.angle);
            ctx.globalAlpha = opacity;
            ctx.fillStyle = p.color;
            if (p.shape === 'rect') {
              ctx.fillRect(-p.r / 2, -p.r / 2, p.r, p.r * 0.55);
            } else {
              ctx.beginPath();
              ctx.arc(0, 0, p.r * 0.45, 0, Math.PI * 2);
              ctx.fill();
            }
            ctx.restore();
          }
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },

      // Count-up animation disabled — race conditions between Alpine paint and
      // the timer caused the final value to occasionally lock in mid-animation,
      // producing wrong numbers on refresh. Static Liquid values render instantly.
      _animateHeroStatsIfStep5() {},

      // ---- navigation ----
      goToStep(n) {
        if (n < 1 || n > 5) return;
        if (n < this.currentStep) {
          this.currentStep = n;
          writeStr(SS_KEY_STEP, String(n));
        }
        // Forward-nav blocked here; each step's Continue handler advances explicitly.
      },
      advanceTo(n) {
        if (n < 1 || n > 5) return;
        this.currentStep = n;
        writeStr(SS_KEY_STEP, String(n));
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this._weglotRescan();
      },

      // Step 4 → Step 5: full page reload so Liquid renders fresh BV/PV/GV from cart metafields.
      // sessionStorage preserves wizard state; URL params keep dev-theme preview working.
      goToReview() {
        if (this.isMutating) return;
        writeStr(SS_KEY_STEP, '5');
        const url = new URL(window.location.href);
        url.searchParams.set('step', '5');
        url.searchParams.set('view', 'affiliate-registration');
        window.location.href = url.toString();
      },

      goToCheckout() {
        if (this.isMutating) return;
        // Clear the "wizard return" marker so Bundle-Builder doesn't redirect back after checkout.
        try { sessionStorage.removeItem(SS_KEY_RETURN); } catch (e) {}
        window.location.href = '/checkout';
      },

      // Step 5 qty change: mutate cart then reload page so server-rendered totals refresh.
      async updateAddonQtyAndReload(variantId, newQty) {
        await this.updateAddonQty(variantId, newQty);
        if (this.isMutating) return; // updateAddonQty bailed
        const url = new URL(window.location.href);
        url.searchParams.set('step', '5');
        url.searchParams.set('view', 'affiliate-registration');
        window.location.href = url.toString();
      },

      // ---- cart sync helpers ----
      async refreshCart() {
        try { this.cart = await fetchCart(); }
        catch (e) { console.warn('[Affiliate Wizard] cart refresh failed', e); }
      },

      // ---- Step 1: Country (dropdown + Continue) ----
      onCountrySubmit(e) {
        // Always prevent the localization form from submitting — we do the country
        // bookkeeping in JS and advance to Step 2 without a page reload.
        if (e && typeof e.preventDefault === 'function') e.preventDefault();

        if (!this.countrySelection) return;

        // Cross-store handoff — different Shopify store, must navigate
        if (this.countrySelection === '__crossstore__') {
          if (!this.config.crossStoreRedirectUrl) {
            this.banner = S('crossstore_unconfigured', 'Cross-store option is not configured. Tell the merchant.');
            return;
          }
          const params = new URLSearchParams();
          if (this.sponsor) {
            if (this.sponsor.refID) params.set('refID', this.sponsor.refID);
            if (this.sponsor.slug) params.set('slug', this.sponsor.slug);
          }
          const side = readCookie('side');
          if (side) params.set('side', side);
          if (this.config.crossStoreCountryCode) {
            params.set('selectedCountry', this.config.crossStoreCountryCode);
            params.set('marketSelected', 'true');
          }
          const sep = this.config.crossStoreRedirectUrl.indexOf('?') >= 0 ? '&' : '?';
          window.location.href = this.config.crossStoreRedirectUrl + sep + params.toString();
          return;
        }

        // Same-store: record country, write lang attrs, advance immediately. No reload.
        const iso = this.countrySelection.toUpperCase();
        this.country = iso;
        writeStr(SS_KEY_COUNTRY, iso);
        if (this.config.lang) {
          // Idempotent fire-and-forget; doesn't block advance
          this.writeCountryAttrs(this.config.lang).catch(err => {
            console.warn('[Affiliate Wizard] lang attr write failed', err);
          });
        }
        this.advanceTo(2);
      },
      async writeCountryAttrs(lang) {
        // Stamp country alongside lang so Step 5 Liquid can compute PV/GV for the
        // user-selected market (not whatever geo Shopify auto-detected).
        const attrs = { lang: lang, 'Invoice Language': lang };
        if (this.country) attrs.country = this.country;
        await updateCartAttributes(attrs);
        await this.refreshCart();
      },

      // ---- Step 2: Sponsor ----
      // Update the global "You're shopping with X" announcement bar (rendered by
      // header.liquid into #replicatedInfo) so a sponsor change reflects immediately
      // without a page reload. If the bar wasn't created on page-load (no stored
      // referredBy at the time), we create it now in the same container.
      _updateReferralBar() {
        if (!this.sponsor) return;
        const fullName = String(this.sponsor.fullName || this.sponsor.slug || '');
        if (!fullName) return;
        const text = S('shopping_with', "You're shopping with [name]", { name: fullName });
        let bar = document.getElementById('referralBar');
        if (bar) {
          bar.textContent = text;
          return;
        }
        const container = document.getElementById('replicatedInfo');
        if (!container) return;
        bar = document.createElement('div');
        bar.id = 'referralBar';
        bar.textContent = text;
        container.innerHTML = '';
        container.appendChild(bar);
      },

      _sponsorDebounceTimer: null,
      onSponsorQueryInput() {
        this.sponsorError = '';
        this.sponsorManuallyEntered = true;
        const q = (this.sponsorQuery || '').trim();
        if (this._sponsorDebounceTimer) clearTimeout(this._sponsorDebounceTimer);
        // Accept Dealer ID / Username / Email. Require >=3 chars, OR an email-looking value.
        const looksEmail = q.includes('@');
        if (!q || (q.length < 3 && !looksEmail)) {
          this.sponsorLoading = false;
          this.sponsor = null;
          return;
        }
        this._sponsorDebounceTimer = setTimeout(() => this.searchSponsor(q), 400);
      },
      async _lookupSponsor(query) {
        const q = String(query || '').trim();
        if (!q) return null;
        const base = String(this.config.apiBase || '').replace(/\/+$/, '');
        let url;
        if (q.includes('@')) {
          url = base + '/account/enroll/getDealerDetailShopifyEmail/' + encodeURIComponent(q);
        } else if (/^\d+$/.test(q)) {
          url = base + '/account/enroll/getDealerDetailShopify/' + encodeURIComponent(q);
        } else {
          url = base + '/account/enroll/getDealerDetailShopifySlug/' + encodeURIComponent(q);
        }
        try {
          const res = await fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' } });
          if (!res.ok) return null;
          const data = await res.json();
          if (!data || data.status !== 1) return null;
          return {
            refID: String(data.setRefID || data.dealerID || q),
            slug: data.slug || '',
            fullName: data.fullName || data.slug || q,
          };
        } catch (e) {
          console.warn('[Affiliate Wizard] sponsor lookup error', e);
          return null;
        }
      },
      async searchSponsor(userID) {
        this.sponsorLoading = true;
        this.sponsorError = '';
        try {
          const s = await this._lookupSponsor(userID);
          if (!s) {
            this.sponsor = null;
            this.sponsorError = S('sponsor_not_found', "We couldn't find a referrer with that Dealer ID, Username, or Email. Check it and try again.");
            return;
          }
          this.sponsor = s;
        } catch (e) {
          console.warn('[Affiliate Wizard] sponsor search error', e);
          this.sponsor = null;
          this.sponsorError = S('sponsor_lookup_fail', "We're having trouble looking up your sponsor. Try again or contact support.");
        } finally {
          this.sponsorLoading = false;
        }
      },
      async confirmSponsor() {
        if (!this.sponsor || !this.sponsor.refID) {
          this.sponsorError = S('no_sponsor', 'No sponsor selected.');
          return;
        }
        if (this.isMutating) return;
        this.banner = '';
        this.isMutating = true;
        try {
          await updateCartAttributes({
            refID: String(this.sponsor.refID),
            referredBy: String(this.sponsor.fullName || this.sponsor.slug),
            referrer_Slug: String(this.sponsor.slug),
            referrer_confirmed: 'true',
            _referral_source: 'wizard',
            _referral_wizard_manual: this.sponsorManuallyEntered ? 'true' : 'false'
          });
          setCookie('refID', String(this.sponsor.refID), 30);
          setCookie('slug', String(this.sponsor.slug), 30);
          setCookie('referredBy', String(this.sponsor.fullName || this.sponsor.slug), 30);
          writeJSON(SS_KEY_SPONSOR, this.sponsor);
          // header.liquid locks the URL refID (URL-first getRefIDFromAllSources + a patched
          // history.replaceState that re-forces it), so a soft replaceState is reverted and a
          // refresh re-applies the stale ?refID= (re-syncing cart/cookies to the OLD referrer).
          // When the referrer was actually CHANGED, navigate (full load) so the new refID wins
          // everywhere and survives refresh. Unchanged confirm stays a smooth in-page advance.
          const _urlRef = (new URLSearchParams(window.location.search).get('refID') || '').trim();
          if (String(this.sponsor.refID) !== _urlRef) {
            const u = new URL(window.location.href);
            u.searchParams.set('refID', String(this.sponsor.refID));
            if (this.sponsor.slug) { u.searchParams.set('slug', String(this.sponsor.slug)); }
            else { u.searchParams.delete('slug'); }
            u.searchParams.set('step', '3');
            window.location.href = u.toString();
            return;
          }
          this._updateReferralBar();
          await this.refreshCart();
          this.sponsorMode = 'confirm';
          this.advanceTo(3);
        } catch (e) {
          console.warn('[Affiliate Wizard] confirm sponsor failed', e);
          this.banner = S('sponsor_save_fail', "Couldn't save your sponsor. Try again.");
        } finally {
          this.isMutating = false;
        }
      },

      addons: {},            // variant_id (string) → qty in cart (cart-derived, used by Step 5 only)
      addonInputs: {},       // variant_id (string) → qty user typed in stepper (LOCAL, not cart)
      addonFeedback: {},     // variant_id (string) → 'onetime' | 'sub' (transient, drives Added ✓ flash)
      activeCollection: null, // current tab id or '__featured'

      // ---- Step 3: Pack select ----
      modalOpen: false,
      modalContent: '',
      modalPackBlockId: null,
      modalPackVariantId: null,
      modalPackTier: '',
      modalPackIncludesScanner: false,

      packCardVisible(el) {
        if (!this.country) return false;
        const showOnly = (el.dataset.showOnlyCountries || '').trim();
        const hideIn = (el.dataset.hideInCountries || '').trim();
        const iso = this.country.toUpperCase();
        if (showOnly) {
          const allow = showOnly.toUpperCase().split(',').map(s => s.trim()).filter(Boolean);
          if (allow.indexOf(iso) === -1) return false;
        }
        if (hideIn) {
          const block = hideIn.toUpperCase().split(',').map(s => s.trim()).filter(Boolean);
          if (block.indexOf(iso) !== -1) return false;
        }
        return true;
      },

      openPackModal(blockId) {
        const tpl = document.querySelector('[data-modal-for="' + blockId + '"]');
        if (!tpl) return;
        const card = document.querySelector('[data-pack-id="' + blockId + '"]');
        this.modalPackBlockId = blockId;
        this.modalPackVariantId = card ? card.dataset.variantId : null;
        this.modalPackTier = card ? (card.dataset.packTier || '') : '';
        this.modalPackIncludesScanner = card ? card.dataset.includesScanner === 'true' : false;
        const modal = document.getElementById('affreg-pack-modal');
        if (!modal) return;
        modal.querySelector('[data-affreg-modal-body]').innerHTML = tpl.innerHTML;
        modal.style.display = 'flex';
        // Save scroll position then lock page (position: fixed approach prevents iOS rubberband too)
        this._savedScrollY = window.scrollY;
        document.documentElement.classList.add('affreg-modal-open');
        document.body.classList.add('affreg-modal-open');
        document.body.style.top = '-' + this._savedScrollY + 'px';
      },
      _hideModal() {
        const modal = document.getElementById('affreg-pack-modal');
        if (modal) {
          modal.style.display = 'none';
          modal.querySelector('[data-affreg-modal-body]').innerHTML = '';
        }
        document.documentElement.classList.remove('affreg-modal-open');
        document.body.classList.remove('affreg-modal-open');
        document.body.style.top = '';
        if (typeof this._savedScrollY === 'number') {
          window.scrollTo(0, this._savedScrollY);
          this._savedScrollY = null;
        }
        this.modalPackBlockId = null;
        this.modalPackVariantId = null;
        this.modalPackTier = '';
        this.modalPackIncludesScanner = false;
      },
      closeModal() { this._hideModal(); },
      async chooseFromModal() {
        if (!this.modalPackVariantId) return;
        const variantId = this.modalPackVariantId;
        const tier = this.modalPackTier || '';
        const includesScanner = this.modalPackIncludesScanner;
        const btn = document.querySelector('#affreg-pack-modal [data-affreg-modal-choose]');
        if (btn) btn.disabled = true;
        this._hideModal();
        try { await this._addPrebuiltPack(variantId, tier, includesScanner); } finally { if (btn) btn.disabled = false; }
      },

      async choosePrebuiltPack(el) {
        const card = el.closest('.affreg-pack-card');
        const variantId = card.dataset.variantId;
        const tier = card.dataset.packTier || '';
        const includesScanner = card.dataset.includesScanner === 'true';
        await this._addPrebuiltPack(variantId, tier, includesScanner);
      },

      // Remove the chosen pack from the order on Step 5. Keeps the membership in cart.
      // Effectively transitions the user to the membership-only path.
      async removePackFromOrder() {
        if (this.isMutating) return;
        if (!confirm(S('confirm_remove_pack', 'Remove your Welcome Pack? Your membership and any add-ons will stay in your cart.'))) return;
        this.banner = '';
        this.isMutating = true;
        this.loadingMessage = S('removing_pack', 'Removing your pack…');
        // Persist intent FIRST so even if anything below partially fails / reloads,
        // the user lands on Step 5 in membership-only mode instead of bouncing.
        writeStr(SS_KEY_PACK, 'membership-only');
        writeStr(SS_KEY_STEP, '5');
        this.packChosen = 'membership-only';
        window.__wizardSwapInProgress = true;
        try {
          // 1. Clear bundle cart attrs FIRST so theme's syncBundleAttributes won't re-stamp the pack.
          try {
            await updateCartAttributes({
              bundle_tier_name: '',
              bundle_tier_variant_id: '',
              bundle_tier_product_id: '',
              bundle_pack: '',
              bundle_product_id: ''
            });
          } catch (e) { console.warn('[Affiliate Wizard] attr clear failed', e); }

          // 2. Get pack lines + scanner lines from a fresh cart.
          //    Scanners are tied to pack-tier qualification — removing the pack should remove them too.
          await this.refreshCart();
          const reg = this.config.scannerRegular;
          const elite = this.config.scannerElite;
          const keysToRemove = ((this.cart && this.cart.items) || [])
            .filter(li => {
              const p = li.properties || {};
              if (p._bundle_pack || p._bbg_builder === '1' || p._bbg_builder === 1 || p._bbg_builder === true) return true;
              if (reg && (li.variant_id == reg.variantId || li.product_id == reg.productId)) return true;
              if (elite && (li.variant_id == elite.variantId || li.product_id == elite.productId)) return true;
              return false;
            })
            .map(li => li.key)
            .filter(Boolean);

          // 3. Remove each line individually via /cart/change.js — more reliable than batch update.
          for (const key of keysToRemove) {
            try {
              await fetch('/cart/change.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ id: key, quantity: 0 })
              });
            } catch (e) { console.warn('[Affiliate Wizard] change failed for key', key, e); }
          }

          // 4. Give theme's syncBundleAttributes time to settle (~600ms), then reload.
          //    The flag stays set so any in-flight ops bypass the watcher.
          await new Promise(r => setTimeout(r, 650));
          window.location.reload();
        } catch (e) {
          console.warn('[Affiliate Wizard] removePackFromOrder failed', e);
          // Even on error, reload so user sees fresh state (cart might be partially updated).
          window.location.reload();
        }
      },

      // Membership-only path: no pack, just enroll. Clear any pack lines and skip to review.
      async chooseMembershipOnly() {
        if (this.isMutating) return;
        this.banner = '';
        this.isMutating = true;
        this.loadingMessage = S('setting_membership', 'Setting up your membership…');
        window.__wizardSwapInProgress = true;
        try {
          await this._clearPackLines();
          // Also remove any scanner in cart — scanner is tied to pack qualification.
          await this._removeScannerFromCart();
          await this._ensureMembershipInCart();
          // Clear bundle_tier_name so Step 5 doesn't apply tier-bonus volumes.
          try { await updateCartAttributes({ bundle_tier_name: '', bundle_tier_variant_id: '', bundle_tier_product_id: '' }); } catch (e) {}
          await this.refreshCart();
          writeStr(SS_KEY_PACK, 'membership-only');
          this.packChosen = 'membership-only';
          this._pulse(40);
          this.advanceTo(5);
        } catch (e) {
          console.warn('[Affiliate Wizard] membership-only enroll failed', e);
          const detail = (e && e.message) ? e.message : '';
          this.banner = detail
            ? S('enroll_fail_detail', "Couldn't enroll you in the membership: [detail]", { detail: detail })
            : S('enroll_fail', "Couldn't enroll you in the membership. Try again.");
        } finally {
          window.__wizardSwapInProgress = false;
          this.isMutating = false;
          this.loadingMessage = '';
        }
      },
      async _addPrebuiltPack(variantId, tier, includesScanner) {
        if (this.isMutating) return;
        this.banner = '';
        this.isMutating = true;
        this.loadingMessage = S('adding_pack', 'Adding your pack to cart…');
        // Signal to wizard-cart-watcher that an authorized swap is in progress so its
        // "Removing pack will clear cart" guard doesn't fire on our own pre-clear /cart/change.
        window.__wizardSwapInProgress = true;
        try {
          // 1. Pre-clear any existing pack lines (prebuilt _bundle_pack OR custom _bbg_builder).
          //    Without this, theme's validateCartAdd shows a swap/conflict modal that the user
          //    must accept, AND even after acceptance the old pack is not always removed,
          //    leaving both packs in cart. We guarantee the swap ourselves.
          await this._clearPackLines();

          // 2. Ensure membership is in cart. (Pack-add without membership fires theme's
          //    "Membership Required" modal which redirects to /cart and drops the pack add.)
          //    Throws on failure so we DON'T leave user with pack-no-membership state.
          await this._ensureMembershipInCart();

          // 3. Add the pack. Stamp _bundle_pack explicitly (W2 wrapper does this from a
          //    hardcoded VARIETY_PACK_BUNDLE_MAP, but if the variant isn't in that map we
          //    still want the property — preserves PV/GV computation downstream).
          const packProperties = { _addedAt: String(Date.now()) };
          if (tier) packProperties._bundle_pack = tier;
          await addToCart([{
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: packProperties
          }]);

          // 4. Re-stamp bundle_tier_name AFTER the pack lands. Theme's syncBundleAttributes
          //    fires +500ms after every cart op and wipes bundle_tier_name if no bundle item
          //    is in cart yet. Stamping after the pack-add response guarantees the cart now
          //    has the bundle item, so this value sticks.
          if (tier) {
            try { await updateCartAttributes({ bundle_tier_name: tier, bundle_tier_variant_id: '', bundle_tier_product_id: '' }); }
            catch (e) { console.warn('[Affiliate Wizard] bundle_tier_name re-stamp failed', e); }
          }

          await this.refreshCart();

          if (prebuiltPackInCart(this.cart)) {
            writeStr(SS_KEY_PACK, 'prebuilt');
            this.packChosen = 'prebuilt';
            this._pulse(50);
            // Scanner: swap if wrong-tier scanner already present, else offer.
            // LATAM markets skip the offer modal entirely (scanner not part of LATAM rollout).
            try {
              if (includesScanner) {
                // Pack ships with a scanner: nothing to offer, and _swapScannerIfTierChanged
                // would silently "upgrade" a scanner the pack already contains.
                this.advanceTo(4);
              } else {
                const swapped = await this._swapScannerIfTierChanged(tier);
                if (!swapped && !this._currentScannerInCart() && !this._scannerOfferDismissed()) {
                  this._showScannerOfferModal(tier);
                  // Modal handles its own advanceTo(4) via accept/decline handlers.
                } else {
                  this.advanceTo(4);
                }
              }
            } catch (e) {
              console.warn('[Affiliate Wizard] scanner step failed', e);
              this.advanceTo(4);
            }
          } else {
            this.banner = S('marker_fail', 'Pack added but the cart did not register the bundle marker — please refresh and try again.');
          }
        } catch (e) {
          console.warn('[Affiliate Wizard] pack ATC failed', e);
          // Prefer the underlying Shopify error (Sold out, inventory, blocked, etc.) over a
          // generic message — users were getting "Try again" when the real cause was "Sold out".
          const detail = (e && e.message) ? e.message : '';
          this.banner = detail
            ? S('pack_fail_detail', "Couldn't add this pack: [detail]", { detail: detail })
            : S('pack_fail', "Couldn't add this pack. Try again or pick a different pack.");
        } finally {
          window.__wizardSwapInProgress = false;
          this.isMutating = false;
          this.loadingMessage = '';
        }
      },

      // Remove every existing pack-class line from the cart so a swap starts from clean state.
      // "Pack class" = any line with _bundle_pack (prebuilt) or _bbg_builder (custom-pack items).
      // We DON'T touch the membership product or addon lines — those persist across swaps.
      async _clearPackLines() {
        await this.refreshCart();
        const items = (this.cart && this.cart.items) || [];
        const packKeys = [];
        for (const li of items) {
          const props = li.properties || {};
          if (props._bundle_pack || props._bbg_builder === '1' || props._bbg_builder === 1 || props._bbg_builder === true) {
            if (li.key) packKeys.push(li.key);
          }
        }
        if (packKeys.length === 0) return;
        console.log('[Affiliate Wizard] clearing existing pack lines', packKeys);
        // /cart/update.js with updates map zeroes multiple lines in one round trip.
        try {
          const res = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
              updates: packKeys.reduce((acc, k) => { acc[k] = 0; return acc; }, {})
            })
          });
          if (!res.ok) {
            const txt = await res.text().catch(() => '');
            throw new Error('clear pack failed: ' + res.status + ' ' + txt.slice(0, 120));
          }
          // Also clear bundle_* cart attributes so the next pack's stamp doesn't merge with stale ones.
          try {
            await updateCartAttributes({
              bundle_tier_name: '',
              bundle_tier_variant_id: '',
              bundle_tier_product_id: '',
              bundle_pack: '',
              bundle_product_id: ''
            });
          } catch (e) { console.warn('[Affiliate Wizard] bundle attr clear failed', e); }
          await this.refreshCart();
        } catch (e) {
          // Non-fatal — pack-add may still succeed if cart was already clean.
          console.warn('[Affiliate Wizard] _clearPackLines failed', e);
        }
      },

      async _ensureMembershipInCart() {
        const membershipVariantId = this.config.membershipVariantId;
        const membershipProductId = this.config.membershipProductId;
        if (!membershipVariantId) return;
        await this.refreshCart();
        const hasMembership = this.cart && this.cart.items && this.cart.items.some(li =>
          (membershipProductId && li.product_id === membershipProductId) ||
          li.variant_id === membershipVariantId
        );
        if (hasMembership) return;
        console.log('[Affiliate Wizard] pre-adding membership variant', membershipVariantId);
        // Re-throw on failure: previously this was swallowed, leaving cart with pack-no-membership
        // (a broken affiliate-enrollment state at checkout).
        // _wizard_membership marks this as an ENROLMENT membership (the buyer, or a recruit
        // they are signing up). membership-cart-logic's self-heal must never remove it.
        await addToCart([{ id: parseInt(membershipVariantId, 10), quantity: 1, properties: { _wizard_membership: '1' } }]);
        await this.refreshCart();
      },

      // LATAM market check — scanner offer modal and custom pack are gated out for these countries.
      // Same code list as `country_hide_in` settings in template JSON.
      _isLatamMarket() {
        if (!this.country) return false;
        const LATAM = ['AR','BO','BR','CL','CO','CR','CU','DO','EC','GT','HN','MX','NI','PA','PY','PE','PR','UY','VE','BZ','SV','BB','TT','JM','BS','AG','KN','LC','VC','GD','DM','AW','CW','SX','BQ','AI','VG','KY','TC','MS'];
        return LATAM.indexOf(this.country.toUpperCase()) !== -1;
      },

      // ---- Scanner offer/swap (Step 3 → Step 4 transition) ----
      _scannerTierKindFor(tier) {
        // Returns 'elite' for Elite and Premium packs, 'regular' for Basic/Essential, null otherwise.
        // Premium gets the same scanner as Elite.
        if (!tier) return null;
        const t = String(tier).toLowerCase();
        if (t.indexOf('elite') !== -1) return 'elite';
        if (t.indexOf('premium') !== -1) return 'elite';
        if (t.indexOf('basic') !== -1 || t.indexOf('essential') !== -1 || t.indexOf('premium') !== -1 || t.indexOf('builder') !== -1 || t.indexOf('pro') !== -1) return 'regular';
        return null;
      },
      _scannerConfigFor(kind) {
        if (kind === 'elite') return this.config.scannerElite || null;
        if (kind === 'regular') return this.config.scannerRegular || null;
        return null;
      },
      _currentScannerInCart() {
        // Returns 'elite' | 'regular' | null based on which scanner variant is in cart.
        const items = (this.cart && this.cart.items) || [];
        const reg = this.config.scannerRegular;
        const elite = this.config.scannerElite;
        for (const item of items) {
          if (elite && (item.variant_id == elite.variantId || item.product_id == elite.productId)) return 'elite';
          if (reg && (item.variant_id == reg.variantId || item.product_id == reg.productId)) return 'regular';
        }
        return null;
      },
      _isScannerVariant(variantId) {
        const v = String(variantId);
        const reg = this.config.scannerRegular;
        const elite = this.config.scannerElite;
        return !!((reg && String(reg.variantId) === v) || (elite && String(elite.variantId) === v));
      },
      // Remove any scanner line(s) from cart. Used when the pack is removed —
      // scanner qualification is tied to the pack tier.
      async _removeScannerFromCart() {
        await this.refreshCart();
        const reg = this.config.scannerRegular;
        const elite = this.config.scannerElite;
        const keys = ((this.cart && this.cart.items) || [])
          .filter(li => (reg && (li.variant_id == reg.variantId || li.product_id == reg.productId))
                     || (elite && (li.variant_id == elite.variantId || li.product_id == elite.productId)))
          .map(li => li.key).filter(Boolean);
        for (const key of keys) {
          try {
            await fetch('/cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 })
            });
          } catch (e) { console.warn('[Affiliate Wizard] scanner remove failed', key, e); }
        }
        // Clear dismissal so user can re-pick a pack and get the scanner offer fresh.
        try { sessionStorage.removeItem('vital_wizard_scanner_dismissed'); } catch (e) {}
      },
      _scannerLineKey(kind) {
        const items = (this.cart && this.cart.items) || [];
        const cfg = this._scannerConfigFor(kind);
        if (!cfg) return null;
        for (const item of items) {
          if (item.variant_id == cfg.variantId || item.product_id == cfg.productId) return item.key;
        }
        return null;
      },
      _scannerOfferDismissed() {
        try { return sessionStorage.getItem('vital_wizard_scanner_dismissed') === '1'; }
        catch (e) { return false; }
      },
      _markScannerOfferDismissed() {
        try { sessionStorage.setItem('vital_wizard_scanner_dismissed', '1'); } catch (e) {}
      },

      // If a scanner is in cart and the new tier wants a DIFFERENT one, swap it.
      // Returns true if a swap happened (so caller knows not to also show the offer modal).
      async _swapScannerIfTierChanged(tier) {
        const wantKind = this._scannerTierKindFor(tier);
        const currentKind = this._currentScannerInCart();
        if (!currentKind) return false;            // No scanner in cart — nothing to swap.
        if (!wantKind) return false;               // Tier doesn't have a known scanner — leave alone.
        if (currentKind === wantKind) return false; // Already correct.
        const oldKey = this._scannerLineKey(currentKind);
        const newCfg = this._scannerConfigFor(wantKind);
        if (!oldKey || !newCfg) return false;
        try {
          // Remove old scanner
          await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: oldKey, quantity: 0 })
          });
          // Add new scanner with the same wizard_addon property so it groups under "Individual Products for Today"
          await addToCart([{
            id: parseInt(newCfg.variantId, 10),
            quantity: 1,
            properties: { _wizard_addon: '1', _wizard_scanner: '1', _addedAt: String(Date.now()) }
          }]);
          await this.refreshCart();
          this.banner = wantKind === 'elite'
            ? S('scanner_upgraded', '✓ Your scanner was upgraded to the discounted Elite price.')
            : S('scanner_swapped', '↺ Your scanner was swapped to match your new pack tier.');
          return true;
        } catch (e) {
          console.warn('[Affiliate Wizard] scanner swap failed', e);
          return false;
        }
      },

      _showScannerOfferModal(tier) {
        const kind = this._scannerTierKindFor(tier);
        const cfg = this._scannerConfigFor(kind);
        const modal = document.getElementById('affreg-scanner-modal');
        if (!cfg || !modal) {
          this.advanceTo(4);
          return;
        }
        const popup = (this.config && this.config.scannerPopup) || {};
        const heading = modal.querySelector('[data-affreg-scanner-heading]');
        const message = modal.querySelector('[data-affreg-scanner-message]');
        const image = modal.querySelector('[data-affreg-scanner-image]');
        const priceLabel = modal.querySelector('[data-affreg-scanner-price-label]');
        const price = modal.querySelector('[data-affreg-scanner-price]');
        if (heading) heading.textContent = popup.heading || S('scanner_heading', 'Add the Vital Health Scanner?');
        if (message) message.textContent = kind === 'elite' ? (popup.messageElite || '') : (popup.messageRegular || '');
        if (image) {
          image.src = cfg.image || '';
          image.alt = cfg.title || 'Vital Health Scanner';
        }
        if (priceLabel) priceLabel.textContent = cfg.title || 'Vital Health Scanner';
        if (price) price.textContent = cfg.price || '';

        modal.style.display = 'flex';
        document.documentElement.classList.add('affreg-modal-open');
        this._savedScrollY = window.scrollY;
        document.body.style.top = '-' + this._savedScrollY + 'px';
        document.body.classList.add('affreg-modal-open');

        const acceptBtn = modal.querySelector('[data-affreg-scanner-accept]');
        const declineBtn = modal.querySelector('[data-affreg-scanner-decline]');
        const closeBtn = modal.querySelector('[data-affreg-scanner-close]');

        const cleanup = () => {
          modal.style.display = 'none';
          document.documentElement.classList.remove('affreg-modal-open');
          document.body.classList.remove('affreg-modal-open');
          document.body.style.top = '';
          window.scrollTo(0, this._savedScrollY || 0);
          acceptBtn && (acceptBtn.onclick = null);
          declineBtn && (declineBtn.onclick = null);
          closeBtn && (closeBtn.onclick = null);
        };

        const decline = () => {
          this._markScannerOfferDismissed();
          cleanup();
          this.advanceTo(4);
        };

        const accept = async () => {
          if (acceptBtn) { acceptBtn.disabled = true; acceptBtn.textContent = S('adding', 'Adding…'); }
          try {
            // Race-safe: re-check cart state immediately before adding.
            await this.refreshCart();
            if (this._currentScannerInCart()) {
              this.banner = S('scanner_already', 'A Vital Health Scanner is already in your cart.');
            } else {
              await addToCart([{
                id: parseInt(cfg.variantId, 10),
                quantity: 1,
                properties: { _wizard_addon: '1', _wizard_scanner: '1', _addedAt: String(Date.now()) }
              }]);
              await this.refreshCart();
              this._pulse(40);
            }
          } catch (e) {
            console.warn('[Affiliate Wizard] scanner add failed', e);
            this.banner = S('scanner_add_fail', "Couldn't add the scanner. You can add it later from Step 4.");
          } finally {
            cleanup();
            this.advanceTo(4);
          }
        };

        if (acceptBtn) acceptBtn.onclick = accept;
        if (declineBtn) declineBtn.onclick = decline;
        if (closeBtn) closeBtn.onclick = decline;
      },

      _armMembershipModalAutoConfirm() {
        // Theme shows a "Membership Required" modal when a welcome pack is added without a membership.
        // The modal's "Proceed with Purchase" button triggers the theme's auto-add-membership flow.
        // We watch for it and click it for the user (wizard flow guarantees membership intent).
        if (this._membershipObserver) return; // already armed
        const tryClickProceed = () => {
          const buttons = document.querySelectorAll('button, a, [role="button"]');
          for (const btn of buttons) {
            const text = (btn.textContent || '').trim().toLowerCase();
            if (btn.id === 'proceedPurchaseBtn' || text === 'proceed with purchase' || text.includes('proceed with purchase')) {
              console.log('[Affiliate Wizard] auto-clicking theme membership modal');
              btn.click();
              return true;
            }
          }
          return false;
        };
        // Try immediately in case the modal is already shown (shouldn't be, but cheap)
        if (tryClickProceed()) return;
        const observer = new MutationObserver(() => {
          if (tryClickProceed()) {
            observer.disconnect();
            this._membershipObserver = null;
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        this._membershipObserver = observer;
        // Disarm after 6 seconds — if the modal never appears, fine
        setTimeout(() => {
          if (this._membershipObserver) {
            this._membershipObserver.disconnect();
            this._membershipObserver = null;
          }
        }, 6000);
      },

      async goToCustomPack() {
        if (this.isMutating) return;
        this.isMutating = true;
        this.banner = '';
        this.loadingMessage = S('setup_builder', 'Setting up your pack builder…');
        // Signal an authorized pack-line removal so cart-watcher's guard doesn't fire.
        window.__wizardSwapInProgress = true;
        try {
          // Pre-clear any existing prebuilt pack lines before handing off to Bundle-Builder.
          // Bundle-Builder doesn't clean up _bundle_pack on entry, so without this the user
          // would arrive at custom-pack with the old prebuilt still in cart, end up with
          // both, and Step 5 would sum both packs' PV/GV. Custom-pack-in-progress (_bbg_builder)
          // items are PRESERVED — the user is returning to continue/restart that build.
          await this._clearPrebuiltPackLines();
          // Persist intent + return marker
          writeStr(SS_KEY_PACK, 'custom');
          writeStr(SS_KEY_RETURN, '1');
          const params = new URLSearchParams();
          params.set('wizard_return', '1');
          if (this.sponsor) {
            if (this.sponsor.refID) params.set('refID', this.sponsor.refID);
            if (this.sponsor.slug) params.set('slug', this.sponsor.slug);
          }
          if (this.country) params.set('country', this.country);
          // Preserve theme-preview params so dev-theme testing stays on dev theme.
          // Also stash in sessionStorage in case a downstream redirect drops the query string.
          const cur = new URLSearchParams(window.location.search);
          const previewId = cur.get('preview_theme_id');
          if (previewId) {
            params.set('preview_theme_id', previewId);
            try { sessionStorage.setItem('vital_wizard_preview_theme_id', previewId); } catch (e) {}
          }
          const viewParam = cur.get('view');
          if (viewParam && viewParam !== 'affiliate-registration') params.set('view', viewParam);
          const url = this.config.customPackUrl + '?' + params.toString();
          window.location.href = url;
        } catch (e) {
          console.warn('[Affiliate Wizard] goToCustomPack failed', e);
          this.banner = S('handoff_fail', "Couldn't hand off to the custom pack builder. Please try again.");
          window.__wizardSwapInProgress = false;
          this.isMutating = false;
          this.loadingMessage = '';
        }
        // No finally — navigation succeeded; flags reset on the next page load.
      },

      // Variant of _clearPackLines that only removes PREBUILT pack lines (_bundle_pack),
      // leaving custom-pack in-progress items (_bbg_builder) intact.
      async _clearPrebuiltPackLines() {
        await this.refreshCart();
        const items = (this.cart && this.cart.items) || [];
        const packKeys = [];
        for (const li of items) {
          const props = li.properties || {};
          if (props._bundle_pack) {
            if (li.key) packKeys.push(li.key);
          }
        }
        if (packKeys.length === 0) return;
        console.log('[Affiliate Wizard] clearing existing prebuilt pack lines', packKeys);
        try {
          const res = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
              updates: packKeys.reduce((acc, k) => { acc[k] = 0; return acc; }, {})
            })
          });
          if (!res.ok) throw new Error('clear prebuilt pack failed: ' + res.status);
          // Clear the prebuilt-pack tier attribute so the custom-pack builder can stamp its own.
          try {
            await updateCartAttributes({
              bundle_tier_name: '',
              bundle_tier_variant_id: '',
              bundle_tier_product_id: '',
              bundle_pack: '',
              bundle_product_id: ''
            });
          } catch (e) { console.warn('[Affiliate Wizard] bundle attr clear failed', e); }
          await this.refreshCart();
        } catch (e) {
          console.warn('[Affiliate Wizard] _clearPrebuiltPackLines failed', e);
        }
      },

      // ---- Step 4: Individual products / addons ----
      _syncAddonsFromCart() {
        // Rebuild addons state from current cart, looking for line items with _wizard_addon flag
        const out = {};
        if (this.cart && this.cart.items) {
          for (const li of this.cart.items) {
            const props = li.properties || {};
            if (props._wizard_addon === '1' || props._wizard_addon === 'true') {
              out[String(li.variant_id)] = li.quantity;
            }
          }
        }
        this.addons = out;
      },

      _initActiveCollection() {
        // Start with null — user sees the entry grid of big collection cards first.
        // They click a card to pick a collection, which switches the view to pills + products.
        this.activeCollection = null;
      },

      setActiveCollection(id) { this.activeCollection = id; },

      featuredTabVisible() {
        // Featured tab is visible if there's at least one visible single_product card
        const cards = document.querySelectorAll('.affreg-products-grid:not([data-collection-id]) .affreg-product-card');
        for (const card of cards) {
          if (this.productCardVisible(card)) return true;
        }
        return false;
      },

      collectionTabVisible(tabEl) {
        // Reuse the same country filter logic as pack cards
        return this.packCardVisible(tabEl);
      },

      productCardVisible(cardEl) {
        // Used for single_product cards in Featured tab; uses same country attr pattern
        return this.packCardVisible(cardEl);
      },

      // ---- Step 4 v2: qty stepper + Add-to-cart + AutoShip Monthly ----
      // Local stepper state lives in addonInputs (NOT cart). Buttons read it and add to cart.
      _getInputQty(variantId) {
        const k = String(variantId);
        const v = parseInt(this.addonInputs[k], 10);
        if (!Number.isFinite(v) || v < 1) return 1;
        return Math.min(99, v);
      },
      incrementInput(variantId) {
        const k = String(variantId);
        this.addonInputs[k] = Math.min(99, this._getInputQty(variantId) + 1);
      },
      decrementInput(variantId) {
        const k = String(variantId);
        this.addonInputs[k] = Math.max(1, this._getInputQty(variantId) - 1);
      },
      clampInput(variantId) {
        const k = String(variantId);
        this.addonInputs[k] = this._getInputQty(variantId);
      },

      // ---- Live volume totals (Step 4 bar) ----
      _volFor(variantId) {
        const map = window.AFFREG_VOL_MAP || {};
        const entry = map[variantId] || map[String(variantId)] || {};
        const pv = parseFloat(entry.pv);
        const gv = parseFloat(entry.gv);
        return { pv: Number.isFinite(pv) ? pv : 0, gv: Number.isFinite(gv) ? gv : 0 };
      },
      _pvFor(variantId) { return this._volFor(variantId).pv; },
      _sumVolumes(filterFn) {
        const items = (this.cart && this.cart.items) || [];
        let pv = 0, gv = 0;
        for (const item of items) {
          if (!filterFn(item)) continue;
          const v = this._volFor(item.variant_id);
          const q = item.quantity || 0;
          pv += v.pv * q;
          gv += v.gv * q;
        }
        return { pv: Math.round(pv), gv: Math.round(gv) };
      },
      orderPV()    { return this._sumVolumes(i => !i.selling_plan_allocation).pv; },
      orderGV()    { return this._sumVolumes(i => !i.selling_plan_allocation).gv; },
      autoshipPV() { return this._sumVolumes(i =>  i.selling_plan_allocation).pv; },
      autoshipGV() { return this._sumVolumes(i =>  i.selling_plan_allocation).gv; },

      async addAddonOneTime(variantId, evt)             { return this._addAddon(variantId, null, 'onetime', evt); },
      async addAddonSubscription(variantId, planId, evt) { return this._addAddon(variantId, planId, 'sub', evt); },

      async _addAddon(variantId, sellingPlanId, kind, evt) {
        if (this.isMutating) return;
        let qty = this._getInputQty(variantId);
        // Scanner cap: only 1 in cart total, regardless of where it's added from.
        if (this._isScannerVariant(variantId)) {
          if (this._currentScannerInCart()) {
            this.banner = S('one_scanner_order', 'You can only have one Vital Health Scanner per order.');
            return;
          }
          qty = 1;
        }
        this.banner = '';
        this.isMutating = true;
        this._pulse(20);
        if (evt && evt.target) this._flyToNext(evt.target);
        try {
          const properties = {
            _wizard_addon: '1',
            _addedAt: String(Date.now())
          };
          if (kind === 'sub') properties._wizard_addon_type = 'sub';
          const item = {
            id: parseInt(variantId, 10),
            quantity: qty,
            properties: properties
          };
          if (sellingPlanId) item.selling_plan = parseInt(sellingPlanId, 10);
          await addToCart([item]);
          await this.refreshCart();
          this._syncAddonsFromCart();
          // Brief "Added ✓" feedback + reset stepper to 1.
          const k = String(variantId);
          this.addonFeedback[k] = kind;
          this.addonInputs[k] = 1;
          setTimeout(() => {
            if (this.addonFeedback[k] === kind) this.addonFeedback[k] = '';
          }, 1500);
        } catch (e) {
          console.warn('[Affiliate Wizard] addon add failed', e);
          const detail = (e && e.message) ? e.message : '';
          this.banner = detail
            ? S('item_fail_detail', "Couldn't add that item: [detail]", { detail: detail })
            : S('item_fail', "Couldn't add that item. Try again.");
        } finally {
          this.isMutating = false;
        }
      },

      async updateAddonQty(variantId, newQty, evt) {
        if (this.isMutating) return;
        newQty = Math.max(0, parseInt(newQty, 10) || 0);
        // Scanner cap: never let qty exceed 1.
        if (this._isScannerVariant(variantId) && newQty > 1) {
          this.banner = S('one_scanner', 'Only one Vital Health Scanner per order.');
          newQty = 1;
        }
        const vidKey = String(variantId);
        const prevQty = this.addons[vidKey] || 0;
        if (newQty === prevQty) return;
        const isIncrement = newQty > prevQty;
        if (isIncrement) {
          this._pulse(20);
          if (evt && evt.target) this._flyToNext(evt.target);
        }
        this.banner = '';
        this.isMutating = true;
        try {
          if (prevQty === 0 && newQty > 0) {
            // Add new line item with wizard-addon flag
            await addToCart([{
              id: parseInt(variantId, 10),
              quantity: newQty,
              properties: { _wizard_addon: '1', _addedAt: String(Date.now()) }
            }]);
          } else {
            // Find existing cart line by variant id + wizard-addon flag, change its qty
            await this.refreshCart();
            const line = (this.cart.items || []).find(li =>
              String(li.variant_id) === vidKey &&
              (li.properties && (li.properties._wizard_addon === '1' || li.properties._wizard_addon === 'true'))
            );
            if (line && line.key) {
              await this._changeCartLine(line.key, newQty);
            } else if (newQty > 0) {
              // line missing — re-add
              await addToCart([{
                id: parseInt(variantId, 10),
                quantity: newQty,
                properties: { _wizard_addon: '1', _addedAt: String(Date.now()) }
              }]);
            }
          }
          await this.refreshCart();
          this._syncAddonsFromCart();
        } catch (e) {
          console.warn('[Affiliate Wizard] addon qty update failed', e);
          const detail = (e && e.message) ? e.message : '';
          this.banner = detail
            ? S('update_fail_detail', "Couldn't update that item: [detail]", { detail: detail })
            : S('update_fail', "Couldn't update that item. Try again.");
        } finally {
          this.isMutating = false;
        }
      },

      async _changeCartLine(lineKey, quantity) {
        // Helper that calls /cart/change.js
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity })
        });
        if (!res.ok) throw new Error('cart/change failed: ' + res.status);
        return await res.json();
      },
    };
  };
})();
